import produce from 'immer';
import enviro from 'enviro';
import { List } from 'immutable';
import EventFilter, { InnerEventFilter } from 'automation-ui-enrollment/records/EventFilter';
import { parseEventTypeId } from 'automation-ui-enrollment/utils/eventFilters';
export const toILSFilterBranch = eventFilters => {
  if (!eventFilters) {
    return undefined;
  }
  return eventFilters.reduce((filterBranch, eventFilter) => {
    const innerEventFilter = eventFilter.getEventFilter();
    let eventTypeId = '';
    /* nullish/no filter means "accept all incoming events"
    in ILS event filters/SegmentationFilters this is represented by the "CONSTANT" filter below
    https://hubspot.slack.com/archives/C8S2M6FL7/p1696621042719329 */
    let filters = [{
      filterType: 'CONSTANT',
      shouldAccept: true
    }];
    if (innerEventFilter) {
      const {
        metaType,
        innerId
      } = innerEventFilter.eventTypeId;
      eventTypeId = `${metaType}-${innerId}`;
      if (innerEventFilter.filter) {
        filters = innerEventFilter.filter.filters;
      }
    }
    return produce(filterBranch, draftFilterBranch => {
      draftFilterBranch.filterBranches.push({
        filterBranchOperator: 'AND',
        filterBranchType: 'AND',
        filters: [],
        filterBranches: [{
          coalescingRefineBy: null,
          eventTypeId,
          filterBranchOperator: 'AND',
          filterBranchType: 'UNIFIED_EVENTS',
          filterBranches: [],
          filters
        }]
      });
    });
  }, {
    filterBranches: [],
    filterBranchOperator: 'OR',
    filterBranchType: 'OR',
    filters: []
  });
};
export const toEventFilters = filterBranch => {
  if (!filterBranch) {
    return List();
  }
  return filterBranch.filterBranches.reduce((eventFilters, stagedFilterBranch) => {
    const eventFilterBranchData = stagedFilterBranch.filterBranches[0];

    /* filter needs to be either a property filter, or nullish.
    nullish means "accept all incoming events", which is the same as the "CONSTANT" filter type on ILS event filters
    https://hubspot.slack.com/archives/C8S2M6FL7/p1696621042719329 */
    const filter = eventFilterBranchData.filters[0].filterType === 'CONSTANT' ? null : {
      filterBranches: [],
      filterBranchOperator: 'AND',
      filterBranchType: 'AND',
      filters: eventFilterBranchData.filters
    };
    return eventFilters.push(new EventFilter().setEventFilter(new InnerEventFilter().setIn(['filter'], filter)).setEventTypeId(parseEventTypeId(eventFilterBranchData.eventTypeId)));
  }, List());
};

// https://tools.hubteam.com/puma/framework-app-settings/automationEventTriggers/entries
const getFormSubmissionEventTypeId = () => {
  return enviro.isQa() ? '4-829203' : '4-1639801';
};

// https://tools.hubteam.com/puma/framework-app-settings/automationWaitUntil/entries/e_visited_page/property
const getVisitedPageEventTypeId = () => {
  return enviro.isQa() ? '4-1079' : '4-96000';
};

// TODO later - unclear whether we need to translate these operators or not
// // UnifiedEventsFilter previously just used the wrong operator for some property filters with multi-select
// const fixEqualAnyOperator = (filter: ILSFilter): ILSFilter => {
//   if (
//     'operation' in filter &&
//     'operator' in filter.operation &&
//     'operation' in filter &&
//     'values' in filter.operation &&
//     filter.operation.values.length
//   ) {
//     // use "is any of" instead of "is equal to" for filters w/ multiple values
//     if (filter.operation.operator === 'IS_EQUAL_TO') {
//       // @ts-expect-error thinks it's an operator for a different filter
//       filter.operation.operator = 'IS_ANY_OF';
//       filter.operation.operatorName = 'IS_ANY_OF';
//       // "is none of" instead of "is not equal to" for filters w/ multiple values
//     } else if (filter.operation.operator === 'IS_NOT_EQUAL_TO') {
//       // @ts-expect-error thinks it's an operator for a different filter
//       filter.operation.operator = 'IS_NONE_OF';
//       filter.operation.operatorName = 'IS_NONE_OF';
//     }
//   }

//   return filter;
// };

// __ANY_PAGE was displayed in UnifiedEventsFilter, but isn't valid in the BE and won't be displayed in SegmentationFilters
const removeAnyPageValue = filter => {
  if ('property' in filter && filter.property === 'hs_content_id' && 'operation' in filter && 'values' in filter.operation && filter.operation.values.length) {
    filter.operation.values = filter.operation.values.filter(value => value !== '__ANY_PAGE');
  }
  return filter;
};
const fixOutdatedFilters = filterBranch => {
  const updatedFilterBranches = filterBranch.filterBranches.map(innerFilterBranch => produce(innerFilterBranch, draftFilterBranch => {
    if (draftFilterBranch.filterBranches[0].filterBranchType !== 'UNIFIED_EVENTS') {
      // should never happen
      reportError({
        error: new Error('[Delay until event + SegmentationFilters] filters in unexpected format'),
        extraData: {
          filterBranch,
          currentFilterBranch: draftFilterBranch
        }
      });
      return draftFilterBranch;
    }
    const oldEventTypeId = draftFilterBranch.filterBranches[0].eventTypeId;
    draftFilterBranch.filterBranches[0].filters = draftFilterBranch.filterBranches[0].filters.map(filter => {
      switch (oldEventTypeId) {
        case getVisitedPageEventTypeId():
        case getFormSubmissionEventTypeId():
          {
            filter = removeAnyPageValue(filter);
            break;
          }
        default:
          break;
      }
      return filter;
    });
    return draftFilterBranch;
  }));
  return Object.assign({}, filterBranch, {
    filterBranches: updatedFilterBranches
  });
};
export const translateToSegmentationFiltersFormat = eventFilters => {
  const filterBranch = toILSFilterBranch(eventFilters);
  if (!filterBranch) {
    return filterBranch;
  }
  const updatedIlsFilterBranch = fixOutdatedFilters(filterBranch);
  return updatedIlsFilterBranch;
};